import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Paperclip, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ACCEPT_ATTRIBUTE } from '@/lib/feedback/attachments'
import type { AttachmentError } from '@/lib/feedback/attachments'

interface AttachmentDropzoneProps {
  files: File[]
  maxFiles: number
  errors: AttachmentError[]
  onAdd: (files: File[]) => void
  onRemove: (index: number) => void
}

function FilePreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file.type.startsWith('image/')) return
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  if (!url) return <FileText className="h-8 w-8 text-muted-foreground" />
  return <img src={url} alt={file.name} className="h-10 w-10 rounded object-cover" />
}

export function AttachmentDropzone({
  files,
  maxFiles,
  errors,
  onAdd,
  onRemove,
}: AttachmentDropzoneProps) {
  const { t } = useTranslation('feedback')
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    onAdd(Array.from(event.dataTransfer.files))
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onAdd(Array.from(event.target.files ?? []))
    // Picking the same file again after removing it must fire change again.
    event.target.value = ''
  }

  return (
    <div className="space-y-2">
      <div
        data-testid="feedback-dropzone"
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          'rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground',
          dragging && 'border-primary bg-primary/5',
        )}
      >
        <Paperclip className="mx-auto mb-1 h-4 w-4" />
        {t('attachments.drop')}{' '}
        <button
          type="button"
          className="font-medium text-primary underline-offset-4 hover:underline"
          onClick={() => input.current?.click()}
        >
          {t('attachments.browse')}
        </button>
        <p className="mt-1 text-xs">{t('attachments.limits', { count: maxFiles })}</p>
        <input
          ref={input}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          className="hidden"
          onChange={handleChange}
        />
      </div>
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
            >
              <FilePreview file={file} />
              <span className="flex-1 truncate">{file.name}</span>
              <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-muted"
                aria-label={t('attachments.remove', { name: file.name })}
                onClick={() => onRemove(index)}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {errors.map((error) => (
        <p key={`${error.code}-${error.name}`} className="text-sm text-destructive">
          {t(`attachments.errors.${error.code}`, { name: error.name, count: maxFiles })}
        </p>
      ))}
    </div>
  )
}
